import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FindMeModal from './FindMeModal';
import Wayfinder from './Wayfinder';
import './HeaderBar.css';

function HeaderBar({ user, onLogout }) {
    const navigate = useNavigate();
    const [bookingMenuOpen, setBookingMenuOpen] = useState(false);
    const [adminMenuOpen, setAdminMenuOpen] = useState(false);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [layoutModalOpen, setLayoutModalOpen] = useState(false);
    const [findMeModalOpen, setFindMeModalOpen] = useState(false);
    const [wayfinderOpen, setWayfinderOpen] = useState(false);
    const [pdfZoom, setPdfZoom] = useState(1);
    const [findMeTarget, setFindMeTarget] = useState(null);

    const toggleBookingMenu = () => setBookingMenuOpen((prev) => !prev);
    const toggleAdminMenu = () => setAdminMenuOpen((prev) => !prev);
    const toggleProfileMenu = () => setProfileMenuOpen((prev) => !prev);
    const closeAllMenus = () => {
        setBookingMenuOpen(false);
        setAdminMenuOpen(false);
        setProfileMenuOpen(false);
    };

    const openLayoutModal = () => {
        closeAllMenus();
        setLayoutModalOpen(true);
        setPdfZoom(1);
    };

    const closeLayoutModal = () => {
        setLayoutModalOpen(false);
    };

    const zoomIn = () => {
        setPdfZoom(prev => Math.min(prev + 0.25, 3));
    };

    const zoomOut = () => {
        setPdfZoom(prev => Math.max(prev - 0.25, 0.5));
    };

    const handleWayfinderSelect = (desk) => {
        // Map Wayfinder desk object to FindMeModal person object
        setFindMeTarget({
            name: desk.user_name,
            desk_id: desk.desk_name,
            zone: desk.desk_name.startsWith('W') ? 'WEST' : 'EAST',
            profile_picture: desk.avatar,
            show_avatar: !!desk.avatar,
            user_name: desk.user_name,
            has_booking: true,
            position: desk.position
        });
        setWayfinderOpen(false);
        setFindMeModalOpen(true);
    };

    const resetZoom = () => {
        setPdfZoom(1);
    };

    const handleNavigate = (path, options) => {
        // close menus then navigate. options can include location state (eg. { state: { resetWizard: true } })
        closeAllMenus();
        if (options) navigate(path, options);
        else navigate(path);
    };

    return (
        <header className="header-bar">
            <div className="brand" onClick={() => handleNavigate('/dashboard', { state: { resetWizard: true } })} role="button" tabIndex={0}>
                <img src="/assets/UNICEF_Logo.png" alt="UNICEF logo" className="brand-logo" />
                <span className="brand-title">Desk Booking System</span>
            </div>

            <nav className="nav-actions">
                <button className="nav-btn" onClick={() => handleNavigate('/dashboard', { state: { resetWizard: true } })}>
                    Let's Book
                </button>
                <button className="nav-btn layout-btn" onClick={openLayoutModal}>
                    Layout View
                </button>
                <button className="nav-btn wayfinder-btn" onClick={() => { closeAllMenus(); setWayfinderOpen(true); }} title="Find a colleague">
                    🧭 Find Me
                </button>
                <div className="nav-menu">
                    <button className="nav-btn" onClick={toggleBookingMenu}>
                        My Booking
                    </button>
                    {bookingMenuOpen && (
                        <div className="menu-dropdown">
                            <button onClick={() => handleNavigate('/bookings/manage')}>Manage Bookings</button>
                            <button onClick={() => handleNavigate('/bookings/history')}>View All Bookings</button>
                        </div>
                    )}
                </div>
                {user?.is_admin ? (
                    <div className="nav-menu">
                        <button className="nav-btn admin-btn" onClick={toggleAdminMenu}>
                            Admin ▼
                        </button>
                        {adminMenuOpen && (
                            <div className="menu-dropdown">
                                <button onClick={() => handleNavigate('/admin/dashboard')}>Dashboard</button>
                                <button onClick={() => handleNavigate('/admin')}>Manage System</button>
                                <button onClick={() => handleNavigate('/admin/desk-allocation')}>Desk Allocation</button>
                                <button onClick={() => handleNavigate('/admin/kiosks')}>Kiosk Management</button>
                            </div>
                        )}
                    </div>
                ) : user?.is_unit_admin ? (
                    <div className="nav-menu">
                        <button className="nav-btn admin-btn" onClick={() => handleNavigate('/unit-admin')}>
                            Unit Admin
                        </button>
                    </div>
                ) : null}
            </nav>

            <div className="user-section">
                <div className="nav-menu profile-menu">
                    <button className="profile-trigger" onClick={toggleProfileMenu}>
                        <div className="profile-avatar">
                            {user?.profile_picture ? (
                                <img src={user.profile_picture} alt="Profile" className="avatar-image" />
                            ) : (
                                (user?.name || user?.email || 'U').charAt(0).toUpperCase()
                            )}
                        </div>
                        <div className="user-meta">
                            <span className="user-name">{user?.name || user?.email}</span>
                            <span className="user-unit">{user?.unit ? `Unit: ${user.unit}` : 'Unit not set'}</span>
                        </div>
                    </button>
                    {profileMenuOpen && (
                        <div className="menu-dropdown profile-dropdown">
                            <button onClick={() => handleNavigate('/profile')}>Edit Profile</button>
                            <button onClick={onLogout}>Logout</button>
                        </div>
                    )}
                </div>
            </div>

            {/* Layout View Modal */}
            {layoutModalOpen && (
                <div className="layout-modal-overlay" onClick={closeLayoutModal}>
                    <div className="layout-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="layout-modal-header">
                            <h3>🏢 Office Layout - Full Hotdesk Map</h3>
                            <button className="modal-close-btn" onClick={closeLayoutModal}>
                                ✕
                            </button>
                        </div>
                        <div className="layout-modal-body">
                            <div className="pdf-viewer-container">
                                <object
                                    data={`${window.location.protocol}//${window.location.host}/data/FullHotdesk.pdf#toolbar=1&navpanes=1&scrollbar=1&page=1&view=FitH`}
                                    type="application/pdf"
                                    className="pdf-viewer"
                                >
                                    <embed
                                        src={`${window.location.protocol}//${window.location.host}/data/FullHotdesk.pdf#toolbar=1&navpanes=1&scrollbar=1&page=1&view=FitH`}
                                        type="application/pdf"
                                        className="pdf-viewer"
                                    />
                                    <div className="pdf-fallback">
                                        <p>Unable to display PDF in browser.</p>
                                        <a
                                            href={`${window.location.protocol}//${window.location.host}/data/FullHotdesk.pdf`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="pdf-download-btn"
                                        >
                                            📄 Download Full Layout PDF
                                        </a>
                                    </div>
                                </object>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Find Me Modal */}
            {findMeModalOpen && (
                <FindMeModal
                    key={findMeTarget ? findMeTarget.desk_id : 'default'}
                    onClose={() => {
                        setFindMeModalOpen(false);
                        setFindMeTarget(null);
                    }}
                    initialPerson={findMeTarget}
                />
            )}

            {/* Wayfinder Modal */}
            {wayfinderOpen && (
                <div className="wayfinder-modal-overlay" onClick={() => setWayfinderOpen(false)}>
                    <div className="wayfinder-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="wayfinder-modal-header">
                            <h3>🧭 Find Me</h3>
                            <button className="modal-close-btn" onClick={() => setWayfinderOpen(false)}>
                                ✕
                            </button>
                        </div>
                        <Wayfinder onDeskSelect={handleWayfinderSelect} />
                    </div>
                </div>
            )}
        </header>
    );
}

export default HeaderBar;
